import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import * as api from '@/api/client';

import { apiErrorText } from '../../../lib/describeApiError';
export type DrafterNotesTabProps = {
  matterId: string;
  initialValue: string;
  isFrozen: boolean;
  onSaved: () => Promise<void> | void;
  onError: (msg: string | null) => void;
};

/**
 * Drafter Notes tab — pure relocation of the legacy `DrafterNotesCard`
 * that previously rendered inline in Stage 1's grid-right column. The
 * card body, auto-save behaviour (1.2 s debounce while typing + blur),
 * the dirty-ref guard against stale matter-refresh overwrites, the
 * 4 000-char cap and the test-ids all move verbatim. Save still goes
 * through PUT /api/matter/:id/stage_1/notes via api.updateDrafterNotes
 * — the endpoint is unchanged.
 */
export default function DrafterNotesTab({
  matterId, initialValue, isFrozen, onSaved, onError,
}: DrafterNotesTabProps) {
  const [value, setValue] = useState(initialValue);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);

  // Re-sync if the matter reloads with a different persisted value —
  // protects against stale local edits when the drafter navigates away
  // and back.
  useEffect(() => {
    if (!dirtyRef.current) setValue(initialValue);
  }, [initialValue]);

  const save = async (next: string) => {
    if (isFrozen) return;
    setSaving(true);
    try {
      await api.updateDrafterNotes(matterId, next);
      setSavedAt(Date.now());
      dirtyRef.current = false;
      await onSaved();
    } catch (e: any) {
      onError(apiErrorText(e, 'Failed to save notes'));
    }
    setSaving(false);
  };

  // Debounced auto-save while typing. 1.2s is long enough that the drafter
  // can finish a sentence without a mid-word save.
  useEffect(() => {
    if (!dirtyRef.current) return;
    const t = setTimeout(() => { save(value); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div
      className="border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="drafter-notes-card"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <MessageSquare size={12} /> Your notes &amp; corrections
        </h3>
        <span className="text-[10px] text-zinc-400">
          {saving
            ? 'Saving…'
            : savedAt
              ? `Saved ${new Date(savedAt).toLocaleTimeString()}`
              : isFrozen ? 'Frozen' : 'Optional'}
        </span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-2">
        These notes are <strong>included in the prompts</strong> for Stage 2
        (Novelty &amp; Inventive Step) and Stage 3 (Claim Drafting) as the
        drafter's authoritative context — use them to correct the AI-extracted
        summary, emphasise what matters, or add background the inventor
        mentioned verbally. They are <em>not</em> exported in the final Word
        output.
      </p>
      <p
        data-testid="drafter-notes-explainer"
        className="text-xs text-zinc-500 leading-relaxed mb-2"
      >
        Tell the AI what the extraction got wrong, or what matters more or
        less than it looks — for example &ldquo;the way the legs attach is not
        significant&rdquo;. The Novelty, Inventive Step and Strategy analyses
        weight these notes above the extracted summary. They are read each
        time an analysis is generated, so regenerate after changing them.
      </p>
      <textarea
        data-testid="drafter-notes-input"
        value={value}
        disabled={isFrozen}
        maxLength={4000}
        rows={5}
        placeholder="e.g. 'The extracted Problem understates the sensor drift case — primary use is LNG carriers on 30-day routes, and real-time BOR below 0.08%/day is the key threshold.'"
        onChange={(e) => {
          dirtyRef.current = true;
          setValue(e.target.value);
        }}
        onBlur={() => {
          if (dirtyRef.current) save(value);
        }}
        className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent disabled:bg-zinc-50 resize-y min-h-[6rem]"
      />
      <div className="text-[10px] text-zinc-400 mt-1 text-right">
        {value.length}/4000
      </div>
    </div>
  );
}
